import { useCallback, useEffect, useState } from 'react';
import {
  AppNotification,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  subscribeNotifications,
} from '@/lib/notificationsApi';

/**
 * Live in-app notifications for the signed-in user. Loads the current list on
 * mount and keeps it fresh via an SSE stream from notification-service.
 */
export function useNotifications() {
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await listNotifications();
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {
      /* unauthenticated or service down — leave empty */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Live updates: the stream pushes the latest items + unread count.
    const unsubscribe = subscribeNotifications((data) => {
      setItems(data.items || []);
      setUnread(data.unread || 0);
      setLoading(false);
    });
    return unsubscribe;
  }, [refresh]);

  const markRead = useCallback(async (id: string) => {
    // Optimistic update, then persist.
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try {
      await markNotificationRead(id);
    } catch {
      refresh();
    }
  }, [refresh]);

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnread(0);
    try {
      await markAllNotificationsRead();
    } catch {
      refresh();
    }
  }, [refresh]);

  return { items, unread, loading, refresh, markRead, markAllRead };
}
